import throttle from "lodash.throttle";

import { LoggingSection } from "@multiweb/logging";
import { ILocalizationItem } from "@multiweb/types/contracts";
import { AppContext } from "App/AppContext";
import { CultureInfo } from "Global/Dom/CultureInfo";
import { IWorkerInitializationMessageData } from "OW.Remoting/Workers/Localization/WorkerMessages";
import { Event } from "../../Global/Base/Events";
import { ContextType, GlobalContext } from "../../Global/Base/GlobalContext";
import { Deferred, PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../Global/Base/Utils/StringUtils";
import { Globalization } from "../../Global/Dom/Globalization";
import { LangMessage, LocKeysMessage } from "../Basic/LocalizationsInterfaces";
import { LocalizationUtils } from "./LocalizationUtils";
import { WorkerCommunicator, workerSymbol } from "./WorkerCommunicator";

export interface ILanguageChangedEventData {
    /** Kultura ve standardu ISO 639-1 nebo v kombinaci se zkratkou státu, tedy buď en nebo en-US, en-GB, ... */
    readonly culture: string;
    /** Kód jazyka používaný v TEAF - např. EN, CZ, ... */
    readonly language: string;
}

const LOC_KEY_ATTRIBUTE = "data-lkd";
const TEXT_ATTRIBUTE = "data-lkta";
const DESCRIPTION_ATTRIBUTE = "data-lkda";
const PARAMS_ATTRIBUTE = "data-lkprms";
const ALWAYS_LOAD_CLASSNAME = "loc-always-load";
const NOT_LOADED_CLASSNAME = "loc-loading";
const PROCESSING_LOCALIZATION_THROTTLE_MS = 100;

export class LocalizationWorkerConnector {
    private static _domChangedObserver: MutationObserver;
    private static _domTextsUpdate: Promise<void>;
    private static localizationNeeded: boolean = false;
    private static _workerLoadedDeferred: Deferred<void> = PromiseUtils.defer();
    private static _languageConfiguredDeferred: Deferred<void> = PromiseUtils.defer();
    private static _pendingLocalizations = new Map<string, ILocalizationItem>();

    private static _throttledLocalizeElements = throttle(() => {
        const localizations = LocalizationWorkerConnector._pendingLocalizations;
        LocalizationWorkerConnector._pendingLocalizations = new Map();
        LocalizationWorkerConnector._localizeElements(localizations);
    }, PROCESSING_LOCALIZATION_THROTTLE_MS);

    private static get _initialized(): Promise<void> {
        return Promise.all([this._workerLoadedDeferred.promise, this._languageConfiguredDeferred.promise]).then(() => { /* Prázdná arrow funkce */ });
    }

    private static _workerCommunicator: WorkerCommunicator;

    public static get workerCommunicator(): WorkerCommunicator {
        return LocalizationWorkerConnector._workerCommunicator;
    }

    public static readonly languageChanged = new Event<typeof Localization, ILanguageChangedEventData>();

    public static async initialize(webServiceUrl: string, appId: string): Promise<void> {
        const w = await WorkerCommunicator.registerWorker("js/TescoSW.Worker.Localization.Loader.js", async () => {
            if (self === top)
                return null;
            const localization: any = GlobalContext.getType<typeof Localization>(Localization, ContextType.top);
            if (localization._workerLoaded)
                await localization._workerLoaded;
            return localization[workerSymbol];
        });

        LocalizationWorkerConnector._workerCommunicator = w;

        LocalizationWorkerConnector._domChangedObserver = new MutationObserver(LocalizationWorkerConnector._localizeDomElements);
        LocalizationWorkerConnector._domChangedObserver.observe(document, { childList: true, subtree: true });

        // zahájí inicializaci workeru s předanou url pro komunikaci
        const message: {
            type: "initialize";
            data: IWorkerInitializationMessageData;
        } = {
            type: "initialize",
            data: {
                communicationUrl: webServiceUrl,
                useLegacyApi: false,
                appId: appId,
                loggingSettings: AppContext.instance.configuration.logging,
                loggingSection: LoggingSection.localizations,
            },
        };
        LocalizationWorkerConnector._workerCommunicator.sendOneWayMessage(message);

        // kdyby náhodou byly elementy s lokalizačními atributy přidány do DOMu ještě před inicializací lokalizačního systému
        LocalizationWorkerConnector._localizeDomElements();

        LocalizationWorkerConnector._workerLoadedDeferred.resolve();
    }

    /**
     * Při změně v DOMu se požádá o zlokalizování chybějících lokalizací
     */
    private static _localizeDomElements(): void {
        LocalizationWorkerConnector.localizationNeeded = true;

        if (LocalizationWorkerConnector._domTextsUpdate) {
            return;
        }

        // update se dělá jednou za x ms
        LocalizationWorkerConnector._domTextsUpdate = PromiseUtils.delay(100).then(async () => {
            const elements = getUnlocalizedElements();

            LocalizationWorkerConnector.localizationNeeded = false;

            const missingLocalizations: string[] = [];
            for (const element of elements) {
                missingLocalizations.push(element.getAttribute(LOC_KEY_ATTRIBUTE));
            }

            if (missingLocalizations.length > 0) {
                await LocalizationWorkerConnector.sendLocalizationRequest(missingLocalizations);
            }            

            LocalizationWorkerConnector._domTextsUpdate = null;

            if (LocalizationWorkerConnector.localizationNeeded) {
                LocalizationWorkerConnector._localizeDomElements();
            }
        });
    }

    public static async sendLocalizationRequest(keys: string[]): Promise<void> {
        await this._initialized;
        const localizations: readonly ILocalizationItem[] = await this._workerCommunicator.sendMessage(this._createLocalizationRequest(keys));

        localizations.forEach(li => this._pendingLocalizations.set(li.locKey, li));
        this._throttledLocalizeElements();
    }

    public static async createLocalizationWaiter(keys: string[]): Promise<ILocalizationItem[]> {
        await this._initialized;
        return LocalizationWorkerConnector._workerCommunicator.sendMessage(LocalizationWorkerConnector._createLocalizationRequest(keys));
    }

    public static sendLangChangeRequest(lang: string): void {
        const msg: LangMessage = { type: "lang", data: { language: lang } };
        this._workerCommunicator.sendOneWayMessage(msg);
        this._languageConfiguredDeferred.resolve();
    }

    public static processElementLocalization(element: Element, li: ILocalizationItem): void {
        const locParams = LocalizationUtils.parseLocalizationParams(element.getAttribute(PARAMS_ATTRIBUTE));
        this._setTextToAttributes(element, element.getAttribute(TEXT_ATTRIBUTE), LocalizationUtils.applyLocParams(li.primaryText, locParams));
        this._setTextToAttributes(element, element.getAttribute(DESCRIPTION_ATTRIBUTE), LocalizationUtils.applyLocParams(li.secondaryText, locParams));
        element.classList.remove(NOT_LOADED_CLASSNAME);
    }

    private static _createLocalizationRequest(keys: string[]): LocKeysMessage {
        return { type: "locKeys", data: { locKeys: keys } };
    }

    private static _localizeElements(locItems: Map<string, ILocalizationItem>): void {
        const elements = getUnlocalizedElements();
        for (const element of elements) {
            const locKey = element.getAttribute(LOC_KEY_ATTRIBUTE);
            const li = locItems.get(locKey);
            if (li) {
                LocalizationWorkerConnector.processElementLocalization(element, li);
            }
        }
    }

    private static _setTextToAttributes(element: Element, attributes: string, text: string): void {
        if (!element || !attributes) {
            return;
        }
        LocalizationUtils.parseAttributesToSet(attributes).forEach(attr => {
            attr = attr.trim();
            if (attr === Localization.TEXT_CONTENT) {   
                const htmlText = StringUtils.stringToHTMLText(text);  
                if (!element.classList.contains(ALWAYS_LOAD_CLASSNAME) || element.innerHTML != htmlText)
                {
                    element.innerHTML = htmlText;
                }
                return;
            }
            
            if (text) {
                element.setAttribute(attr, text);
            }
        });
    }
}

/**
 * Lokalizační třída, umožňuje ručně načítat texty pro lokalizační klíče, automaticky lokalizovat elementy s lokalizačními atributy a měnit jazyk překladů.
 * @includeToDoc
 */
export class Localization {
    public static readonly TEXT_CONTENT: string = "tc";

    // @ts-ignore // nevyužito, ale ponecháno
    private static get [workerSymbol](): WorkerCommunicator {
        return LocalizationWorkerConnector.workerCommunicator;
    }
    //------------------------------------------------------------------------------------------------------------------------

    /**
     * Událost vyvolaná při změně jazyka
     */
    public static get languageChanged(): Event<typeof Localization, ILanguageChangedEventData> {
        return LocalizationWorkerConnector.languageChanged;
    }

    /**
     * Ziniciuje sledování a načítání lokalizací
     */
    public static initialize(webServiceUrl: string, appId: string): Promise<void> {
        return LocalizationWorkerConnector.initialize(webServiceUrl, appId);
    }

    /**
     * Nastaví jazyk při inicializaci aplikace
     * @param code Kód jazyka v TescoSW formátu
     * @param culture Kultura ve standardu ISO 639-1 nebo v kombinaci se zkratkou státu, tedy buď en nebo en-US, en-GB, ...
     */
    public static setLanguage(code: string, culture: string): void {
        if (!code || !culture)
            throw new Error("Language or culture parameter undefined");

        LocalizationWorkerConnector.sendLangChangeRequest(code);

        CultureInfo.setCulture(culture);
        LocalizationWorkerConnector.languageChanged.trigger(this, { culture, language: code });

        Localization._setHtmlLangAttribute();
    }

    /**
     * Nastaví nový jazyk za běhu aplikace a spustí změnu překladů u stávajících elementů
     * @param code Kód jazyka používaný v TEAF - např. EN, CZ, ...
     * @param culture Kultura ve standardu ISO 639-1 nebo v kombinaci se zkratkou státu, tedy buď en nebo en-US, en-GB, ...
     */
    public static changeLanguage(code: string, culture: string): void {
        this.setLanguage(code, culture);

        const keysForLocalization: string[] = [];
        const els = getElementsWithLocalizationData();
        for (const el of els) {
            keysForLocalization.push(el.getAttribute(LOC_KEY_ATTRIBUTE));
            el.classList.add(NOT_LOADED_CLASSNAME); // lokalizace nenačtena
        }
        LocalizationWorkerConnector.sendLocalizationRequest(keysForLocalization);
    }

    /**
     * Nastaví kód jazyka do atributu _lang_ dokumentu.
     * https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
     */
    private static _setHtmlLangAttribute(): void {
        const twoLetterISOLangCode = Globalization.actualCultureISO639_1.toLowerCase();

        if (!twoLetterISOLangCode)
            return;

        document.documentElement.lang = twoLetterISOLangCode;
    }

    //#region Methods

    /**
     *
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param elementType jmeno elementu ktery se ma vytvorit
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     */
    public static getTextElement(locKey: string, elementType: string = "span", locParams?: string[]): HTMLElement {
        if (!locKey)
            throw new TypeError("Invalidate parameter value: locKey");
        locKey = locKey.toUpperCase();

        const result = document.createElement(elementType);
        result.setAttribute(LOC_KEY_ATTRIBUTE, locKey);
        result.setAttribute(TEXT_ATTRIBUTE, Localization.TEXT_CONTENT); // locKey Text Attribute
        if (locParams && locParams.length)
            result.setAttribute(PARAMS_ATTRIBUTE, LocalizationUtils.encodeLocalizationParams(locParams));

        LocalizationWorkerConnector.sendLocalizationRequest([locKey]);
        result.classList.add(NOT_LOADED_CLASSNAME); // lokalizace nenačtena

        return result;
    }

    /**
     *
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param elementName jmeno elementu ktery se ma vytvorit
     * @param txtAttr nazev atributu kde se ma vlozit lokalizovany text, pokud se nevyplni je Localization.TEXT_CONTENT
     * @param dscAttr nazev atributu kde se ma vlozit popisek
     * @param addAttributes pripadne dalsi atributu ktere se maji vlozit do elementu napr. 'class="abc" data-neco="xxx"'
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     */
    public static createLocElementAsString(locKey: string, elementName: string = "span", txtAttr: string = Localization.TEXT_CONTENT, dscAttr?: string, addAttributes?: string, locParams?: string[]): string {
        if (!locKey)
            throw new TypeError("Invalidate parameter value: locKey");
        locKey = locKey.toUpperCase();

        let result: string = `<${elementName} class='${NOT_LOADED_CLASSNAME}' ${LOC_KEY_ATTRIBUTE}='${locKey}' `;
        if (txtAttr)
            result += `${TEXT_ATTRIBUTE}='${txtAttr}' `;

        if (dscAttr)
            result += `${DESCRIPTION_ATTRIBUTE}='${dscAttr}' `;

        if (locParams && locParams.length)
            result += `${PARAMS_ATTRIBUTE}='${LocalizationUtils.encodeLocalizationParams(locParams).replace("'", "\\'")}'`;

        if (addAttributes)
            result += ` ${addAttributes} `;

        LocalizationWorkerConnector.sendLocalizationRequest([locKey]);

        result += `</${elementName}>`;
        return result;
    }

    /**
     * nacte zadany seznam lokalizacnich klicu
     */
    public static loadLocalizations(locKeys: string[]): Promise<ILocalizationItem[]> {
        if (locKeys.length > 0)
            return LocalizationWorkerConnector.createLocalizationWaiter(locKeys);
        else
            return Promise.resolve([]);
    }

    /**
     * Funkce načte lokalizační informace
     * @param locKey klíč k lokalizaci
     */
    public static async getLocalizationItem(locKey: string): Promise<ILocalizationItem> {
        return LocalizationWorkerConnector.createLocalizationWaiter([locKey]).then((li) => li.length ? li[0] : undefined);
    }

    /**
     * Funkce načte seznam lokalizačních informací k seznamu lokalizačních klíčů
     * @desc test do konzole TescoSW.Localization.GetLocalizationItems('gui-3', 'GUi-3').then(v=>console.log(v)).catch(e=>console.error(e))
     * @param locKeys seznam klíčů k lokalizaci
     */
    public static async getLocalizationItems(...locKeys: string[]): Promise<Map<string, ILocalizationItem>> {
        return LocalizationWorkerConnector.createLocalizationWaiter(locKeys).then((li) => li.length ? new Map(li.map((v) => [v.locKey, v])) as any : new Map());
    }

    /**
     * Funkce načte seznam lokalizovaných textů  k seznamu lokalizačních klíčů
     * @desc test do konzole TescoSW.Localization.GetLocalizationItems('gui-3', 'GUi-3').then(v=>console.log(v)).catch(e=>console.error(e))
     * @param locKeys seznam klíčů k lokalizaci
    */
    public static async getTexts(...locKeys: string[]): Promise<Map<string, string>> {
        return LocalizationWorkerConnector.createLocalizationWaiter(locKeys).then((li) => li.length ? new Map(li.map((v) => [v.locKey, v.primaryText])) as any : new Map());
    }

    /**
     * Vrátí promise s lokalizovaným textem (část Text z lokalizace)
     * !!!Tato metoda není doporučována, při jejím použití nebude automaticky fungovat změna lokalizací při změně jazyka!!!
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     */
    public static async getTextPromise(locKey: string): Promise<string> {
        const item = await this.getLocalizationItem(locKey);
        return item ? item.primaryText : undefined;
    }

    /**
     * Vrátí promise s lokalizovaným popiskem (část Description z lokalizace)
     * !!!Tato metoda není doporučována, při jejím použití nebude automaticky fungovat změna lokalizací při změně jazyka!!!
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     */
    public static async getDescriptionPromise(locKey: string): Promise<string> {
        const item = await this.getLocalizationItem(locKey);
        return item ? item.secondaryText : undefined;
    }

    /**
     * Vrátí promise s lokalizovaným textem (část Text z lokalizace) s nahrazenými parametry
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param params pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     */
    public static async getTextPromiseWithParams(locKey: string, params: string[]): Promise<string> {
        return LocalizationUtils.applyLocParams(await this.getTextPromise(locKey), params);
    }

    /**
     * Vrátí promise s lokalizovaným popiskem (část Description z lokalizace) s nahrazenými parametry
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param params pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     */
    public static async getDescriptionPromiseWithParams(locKey: string, params: string[]): Promise<string> {
        return LocalizationUtils.applyLocParams(await this.getDescriptionPromise(locKey), params);
    }

    /**
     * Přidá do elementu atributy sloužící pro asynchronní lokalizaci -
     * přidává část Text z lokalizace do atributu elementu specifikovaného pomocí txtAttr
     * a část Description do atributu elementu specifikovaného pomocí dscAttr
     * @param element element do ktereho ze maji pridat lokalizacni informace
     * @param txtAttr nazev/nazvy atributu, do kterych se ma vlozit lokalizovany text - pokud neni vyplnen nebo ma hodnotu Localization.TEXT_CONTENT tak se vlozi jako tex elementu pres property textContent
     * @param dscAttr nazev/nazvy atributu, do kterych se ma vlozit lokalizacni popisek - pokud neni vyplnen, tak se vloží do atributu 'title', pokud má hodnotu Localization.TEXT_CONTENT, tak se vlozi jako text do elementu pres property textContent
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     * @param alwaysLoad Příznak, zda se mají lokalizace načíst při kažé změně DOM. Použít pouze pro elementy, které jsou v DOMu pouze krátce a hrozí, že v něm nebudou při změně jazyka, jako třeba elementy na Popup/DropdownMenu.
     * @returns Předaný element
     */
    public static addLocData(element: HTMLElement, locKey: string, txtAttr: string | readonly string[] = Localization.TEXT_CONTENT, dscAttr: string | readonly string[] = "title", locParams?: string[], alwaysLoad: boolean = false): HTMLElement {
        if (!locKey)
            return element;
        locKey = locKey.toUpperCase();
        this._setLocAttributes(element, locKey, txtAttr, dscAttr, locParams);

        LocalizationWorkerConnector.sendLocalizationRequest([locKey]);
        element.classList.add(alwaysLoad ? ALWAYS_LOAD_CLASSNAME : NOT_LOADED_CLASSNAME);
        return element;
    }

    /**
     * Přidá do elementu lokalizační atributy, provadi dotazeni lokalizace hned a umoznuje pockat na dobehnuti
     * přidává část Text z lokalizace do atributu elementu specifikovaného pomocí txtAttr
     * a část Description do atributu elementu specifikovaného pomocí dscAttr
     * @param element element do ktereho ze maji pridat lokalizacni informace
     * @param txtAttr nazev/nazvy atributu, do kterych se ma vlozit lokalizovany text - pokud neni vyplnen nebo ma hodnotu Localization.TEXT_CONTENT tak se vlozi jako tex elementu pres property textContent
     * @param dscAttr nazev/nazvy atributu, do kterych se ma vlozit lokalizacni popisek - pokud neni vyplnen, tak se vloží do atributu 'title', pokud má hodnotu Localization.TEXT_CONTENT, tak se vlozi jako text do elementu pres property textContent
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     * @param alwaysLoad Příznak, zda se mají lokalizace načíst při kažé změně DOM. Použít pouze pro elementy, které jsou v DOMu pouze krátce a hrozí, že v něm nebudou při změně jazyka, jako třeba elementy na Popup/DropdownMenu.
     * @returns Předaný element
     */
    public static async addLocDataAsync(element: HTMLElement, locKey: string, txtAttr: string | readonly string[] = Localization.TEXT_CONTENT, dscAttr: string | readonly string[] = "title", locParams?: string[], alwaysLoad: boolean = false): Promise<HTMLElement> {
        if (!locKey)
            return element;
        locKey = locKey.toUpperCase();
        this._setLocAttributes(element, locKey, txtAttr, dscAttr, locParams);

        const locItem = await this.getLocalizationItem(locKey);
        if (locItem) {
            LocalizationWorkerConnector.processElementLocalization(element, locItem);
        }

        if (alwaysLoad)
            element.classList.add(ALWAYS_LOAD_CLASSNAME);

        return element;
    }

    private static _setLocAttributes(element: HTMLElement, locKey: string, txtAttr: string | readonly string[], dscAttr: string | readonly string[], locParams: string[]): void {
        element.setAttribute(LOC_KEY_ATTRIBUTE, locKey);
        element.setAttribute(TEXT_ATTRIBUTE, LocalizationUtils.encodeAttributesToSet(txtAttr));
        if (dscAttr)
            element.setAttribute(DESCRIPTION_ATTRIBUTE, LocalizationUtils.encodeAttributesToSet(dscAttr));
        if (locParams && locParams.length)
            element.setAttribute(PARAMS_ATTRIBUTE, LocalizationUtils.encodeLocalizationParams(locParams));
    }

    /**
     * Odebere z elementu lokalizace
     * @param element element, ze ktereho se maji odebrat lokalizacni informace
     * @param removeContent příznak jesli má v elementu smazat i jeho content
     */
    public static remLocData(element: HTMLElement, removeContent: boolean = true): void {
        const txtAttr = element.getAttribute(TEXT_ATTRIBUTE);
        const descAttr = element.getAttribute(DESCRIPTION_ATTRIBUTE);

        if (removeContent && !txtAttr) {
            element.textContent = "";
        } else {
            Localization._removeAttributeSet(element, txtAttr);
        }
        Localization._removeAttributeSet(element, descAttr);

        element.removeAttribute(LOC_KEY_ATTRIBUTE);
        element.removeAttribute(TEXT_ATTRIBUTE);
        element.removeAttribute(DESCRIPTION_ATTRIBUTE);
        element.removeAttribute(PARAMS_ATTRIBUTE);
        element.classList.remove(NOT_LOADED_CLASSNAME);
        element.classList.remove(ALWAYS_LOAD_CLASSNAME);
    }

    private static _removeAttributeSet(element: HTMLElement, attributes: string): void {
        if (!attributes)
            return;
        LocalizationUtils.parseAttributesToSet(attributes).forEach(attr => {
            attr = attr.trim().toLowerCase();
            if (attr == Localization.TEXT_CONTENT)
                element.textContent = "";
            else
                element.removeAttribute(attr);
        });
    }

    /**
     * Přidá do elementu atributy sloužící pro asynchronní lokalizaci - přidává část Text z lokalizace do atributu elementu specifikovaného pomocí txtAttr
     * @param element element do ktereho ze maji pridat lokalizacni informace
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param txtAttr nazev/nazvy atributu, do kterych se ma vlozit lokalizovany text - pokud neni vyplnen nebo ma hodnotu Localization.TEXT_CONTENT tak se vlozi jako tex elementu pres property textContent
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     * @param alwaysLoad Příznak, zda se mají lokalizace načíst při kažé změně DOM. Použít pouze pro elementy, které jsou v DOMu pouze krátce a hrozí, že v něm nebudou při změně jazyka, jako třeba elementy na Popup/DropdownMenu.
     * @returns Předaný element
     */
    public static addLocTxtData(element: HTMLElement, locKey: string, txtAttr: string | readonly string[] = Localization.TEXT_CONTENT, locParams?: string[], alwaysLoad: boolean = false): HTMLElement {
        if (!locKey)
            return element;
        locKey = locKey.toUpperCase();

        element.setAttribute(LOC_KEY_ATTRIBUTE, locKey);
        element.setAttribute(TEXT_ATTRIBUTE, LocalizationUtils.encodeAttributesToSet(txtAttr));
        if (locParams && locParams.length)
            element.setAttribute(PARAMS_ATTRIBUTE, LocalizationUtils.encodeLocalizationParams(locParams));

        LocalizationWorkerConnector.sendLocalizationRequest([locKey]);
        element.classList.add(alwaysLoad ? ALWAYS_LOAD_CLASSNAME : NOT_LOADED_CLASSNAME);
        return element;
    }

    /**
     * Přidá do elementu atributy sloužící pro asynchronní lokalizaci - přidává část Description z lokalizace do atributu elementu specifikovaného pomocí dscAttr
     * @param element element do ktereho ze maji pridat lokalizacni informace
     * @param locKey lokalizační klíč ve formátu "TYP-XXX"
     * @param dscAttr nazev,nazvy atributu, do kterych se ma vlozit lokalizacni popisek - pokud neni vyplnen nebo ma hodnotu Localization.TEXT_CONTENT tak ze vlozi jako text do elementu pres property textContent
     * @param locParams pole řetězců, kterými se mají postupně nahradit parametry v lokalizovaném textu - @0, @1, ...
     * @param alwaysLoad Příznak, zda se mají lokalizace načíst při kažé změně DOM. Použít pouze pro elementy, které jsou v DOMu pouze krátce a hrozí, že v něm nebudou při změně jazyka, jako třeba elementy na Popup/DropdownMenu.
     * @returns Předaný element
     */
    public static addLocDescData(element: HTMLElement, locKey: string, dscAttr: string | readonly string[] = Localization.TEXT_CONTENT, locParams?: string[], alwaysLoad: boolean = false): HTMLElement {
        if (!locKey)
            return element;
        locKey = locKey.toUpperCase();

        element.setAttribute(LOC_KEY_ATTRIBUTE, locKey);
        if (dscAttr)
            element.setAttribute(DESCRIPTION_ATTRIBUTE, LocalizationUtils.encodeAttributesToSet(dscAttr));
        if (locParams && locParams.length)
            element.setAttribute(PARAMS_ATTRIBUTE, LocalizationUtils.encodeLocalizationParams(locParams));

        LocalizationWorkerConnector.sendLocalizationRequest([locKey]);
        element.classList.add(alwaysLoad ? ALWAYS_LOAD_CLASSNAME : NOT_LOADED_CLASSNAME);
        return element;
    }
    //endregion Methods
}

const getUnlocalizedElements = () => [...document.getElementsByClassName(NOT_LOADED_CLASSNAME), ...document.getElementsByClassName(ALWAYS_LOAD_CLASSNAME)];
const getElementsWithLocalizationData = () => [...document.querySelectorAll(`[${LOC_KEY_ATTRIBUTE}]`)];

GlobalContext.registerType(Localization);
